import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface StripeStats {
  subscriptions: {
    total: number;
    active: number;
    trialing: number;
    canceled: number;
    past_due: number;
  };
  customers: {
    total: number;
  };
  revenue: {
    monthly: number;
    yearly: number;
    recent: number;
  };
  payments: {
    total: number;
    succeeded: number;
    pending: number;
    failed: number;
  };
}

interface UseStripeStatsReturn {
  stats: StripeStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useStripeStats = (): UseStripeStatsReturn => {
  const { user } = useAuth();
  const [stats, setStats] = useState<StripeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const { data, error: fetchError } = await supabase.functions.invoke('admin-stripe-stats', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (fetchError) {
        throw fetchError;
      }

      if (data?.success && data.subscriptions) {
        setStats(data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch Stripe stats';
      setError(errorMessage);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
};

